// VRG-679 — react-query hooks for GTM → Outreach.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { gtmApi, type OutreachCampaignInput, type OutreachTargetInput } from 'services'

const KEY = ['internal', 'gtm', 'outreach'] as const

export const useOutreachCampaigns = () =>
  useQuery({ queryKey: [...KEY, 'campaigns'], queryFn: () => gtmApi.outreachCampaigns(), staleTime: 30_000, retry: false })

export const useOutreachCampaign = (id: string | undefined) =>
  useQuery({
    queryKey: [...KEY, 'campaign', id],
    queryFn: () => gtmApi.outreachCampaign(id as string),
    enabled: !!id,
    staleTime: 15_000,
    retry: false,
  })

/** Every outreach write invalidates outreach, and the channel map + Sales (links write a channel). */
const useInvalidate = () => {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: KEY })
    qc.invalidateQueries({ queryKey: ['internal', 'gtm', 'source-channels'] })
    qc.invalidateQueries({ queryKey: ['internal', 'gtm', 'sales-pipeline'] })
  }
}

export const useOutreachActions = () => {
  const onSuccess = useInvalidate()
  return {
    createCampaign: useMutation({ mutationFn: (i: OutreachCampaignInput) => gtmApi.createOutreachCampaign(i), onSuccess }),
    updateCampaign: useMutation({
      mutationFn: ({ id, input }: { id: string; input: OutreachCampaignInput }) => gtmApi.updateOutreachCampaign(id, input),
      onSuccess,
    }),
    deleteCampaign: useMutation({ mutationFn: (id: string) => gtmApi.deleteOutreachCampaign(id), onSuccess }),
    addTarget: useMutation({
      mutationFn: ({ campaignId, input }: { campaignId: string; input: OutreachTargetInput }) => gtmApi.addOutreachTarget(campaignId, input),
      onSuccess,
    }),
    updateTarget: useMutation({
      mutationFn: ({ id, input }: { id: string; input: OutreachTargetInput }) => gtmApi.updateOutreachTarget(id, input),
      onSuccess,
    }),
    deleteTarget: useMutation({ mutationFn: (id: string) => gtmApi.deleteOutreachTarget(id), onSuccess }),
    linkPerson: useMutation({ mutationFn: (id: string) => gtmApi.linkOutreachPerson(id), onSuccess }),
    unlinkPerson: useMutation({ mutationFn: (id: string) => gtmApi.unlinkOutreachPerson(id), onSuccess }),
    linkDemo: useMutation({
      mutationFn: ({ id, companyId }: { id: string; companyId: string }) => gtmApi.linkOutreachDemo(id, companyId),
      onSuccess,
    }),
    unlinkDemo: useMutation({ mutationFn: (id: string) => gtmApi.unlinkOutreachDemo(id), onSuccess }),
  }
}
